import { SelectModeData } from "PFApp/booking/booking_edit_mode_store";
import { ReassignBookingModal } from "PFApp/booking/components/reassign_booking_modal/reassign_booking_modal";
import {
  INITIAL_STATE,
  ReassignActionApi,
  ReassignActionApiContext,
  ReassignActionState,
  ReassignActionStateContext
} from "PFApp/booking/parts/overview/booking_module_calendar/context/reassign_action_context";
import { ProfileSource } from "PFApp/components/profile_source_toggle";
import { ObjectWithProfileName } from "PFCore/helpers/profile";
import { useProfile } from "PFCore/hooks/queries/profile/use_profile";
import { useIsUnbiasedMode } from "PFCore/hooks/use_is_unbiased_mode";
import { Activity, Availability, Booking } from "PFTypes";
import { PropsWithChildren, useMemo, useReducer } from "react";

import { BookingDetailSidePanelProfile } from "../../detail_view/booking_detail/booking_detail_side_panel_context/booking_detail_side_panel_context.types";

export type ProfileFrom = { id: number } & Partial<ObjectWithProfileName> & {
    email?: string;
    suspendedAt?: string | null;
    isUnbiasedMode?: boolean;
  };

export type ReassignActionData = {
  activityName: string | undefined;
  activity?: Activity;
  sourceProfileId: number;
  sourceProfileIsUnbiasedMode?: boolean;
  targetProfileIsUnbiasedMode?: boolean;
  targetProfileId?: number;
  bookings: Booking[];
  isEngagement?: boolean;
  availability?: Availability;
  suspendedProfileDetails?: BookingDetailSidePanelProfile;
  bulkSelection?: SelectModeData;
  reassignTarget?: ProfileSource;
  onClose?: () => void;
  onSubmit?: () => void;
  onError?: () => void;
};

type Action = { type: "openReassignModal"; data: ReassignActionData } | { type: "closeReassignModal" };

const reassignActionReducer = (state: ReassignActionState, action: Action): ReassignActionState => {
  switch (action.type) {
    case "openReassignModal":
      return { ...state, isModalOpened: true, data: action.data };
    case "closeReassignModal":
      return { ...state, isModalOpened: false, data: null };
    default:
      return state;
  }
};

export const ReassignActionContextProvider = ({ children }: PropsWithChildren) => {
  const [state, dispatch] = useReducer(reassignActionReducer, INITIAL_STATE);
  const isUnbiasedMode = useIsUnbiasedMode(state.data?.activity?.reviewers);

  const { data: targetProfile, isLoading: targetProfileIsLoading } = useProfile(state.data?.targetProfileId, {
    enabled: !!state.data?.targetProfileId
  });

  const suspendedProfileDetails = state.data?.suspendedProfileDetails;
  const { data: sourceProfileData, isLoading: sourceProfileIsLoading } = useProfile(
    state.data?.sourceProfileId,
    {
      enabled: !!state.data?.sourceProfileId && !suspendedProfileDetails
    }
  );

  const sourceProfileIsUnbiasedMode = state.data?.sourceProfileIsUnbiasedMode ?? isUnbiasedMode;
  const targetProfileIsUnbiasedMode = state.data?.targetProfileIsUnbiasedMode ?? isUnbiasedMode;

  const sourceProfile = sourceProfileData
    ? {
        id: sourceProfileData.id,
        firstName: sourceProfileData.firstName,
        lastName: sourceProfileData.lastName,
        email: sourceProfileData.email,
        suspendedAt: sourceProfileData.suspendedAt,
        unbiased: sourceProfileIsUnbiasedMode
      }
    : suspendedProfileDetails;

  const apiValue = useMemo<ReassignActionApi>(() => {
    const openModal = (data: ReassignActionData) => {
      dispatch({ type: "openReassignModal", data });
    };

    const closeModal = () => {
      dispatch({ type: "closeReassignModal" });
    };

    return { openModal, closeModal };
  }, []);

  const stateValue = useMemo(() => state, [state]);

  return (
    <ReassignActionApiContext.Provider value={apiValue}>
      <ReassignActionStateContext.Provider value={stateValue}>
        {children}
        {stateValue.isModalOpened && (
          <ReassignBookingModal
            profileFrom={sourceProfile}
            profileTo={{
              profile: targetProfile,
              isUnbiasedMode: targetProfileIsUnbiasedMode
            }}
            isLoading={targetProfileIsLoading || sourceProfileIsLoading}
          />
        )}
      </ReassignActionStateContext.Provider>
    </ReassignActionApiContext.Provider>
  );
};
